import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface PassphraseDialogProps {
  mode: "unlock" | "setup";
  error: string | null;
  onSubmit: (passphrase: string) => Promise<void>;
}

export function PassphraseDialog({
  mode,
  error,
  onSubmit,
}: PassphraseDialogProps) {
  const [passphrase, setPassphrase] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (mode === "setup" && passphrase !== confirm) {
      setLocalError("Passphrases do not match.");
      return;
    }

    if (passphrase.length < 1) {
      setLocalError("Please enter a passphrase.");
      return;
    }

    setLoading(true);
    try {
      await onSubmit(passphrase);
    } finally {
      setLoading(false);
    }
  };

  const displayError = localError || error;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-8">
      <div className="w-full max-w-sm">
        <h1 className="text-3xl font-bold text-on-surface mb-2 text-center">
          {mode === "unlock" ? "Welcome back" : "Set up recovery"}
        </h1>
        <p className="text-sm text-outline mb-8 text-center">
          {mode === "unlock"
            ? "Enter your recovery passphrase to unlock your notes."
            : "Create a recovery passphrase to protect your encryption key. You'll need this to access your notes on new devices."}
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Input
            type="password"
            placeholder={
              mode === "unlock" ? "Recovery passphrase" : "Create passphrase"
            }
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            autoFocus
            className="h-12 bg-surface-container-highest border-outline-variant rounded-xl text-on-surface placeholder:text-on-surface-variant"
          />

          {mode === "setup" && (
            <Input
              type="password"
              placeholder="Confirm passphrase"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-12 bg-surface-container-highest border-outline-variant rounded-xl text-on-surface placeholder:text-on-surface-variant"
            />
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 text-lg rounded-2xl bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {loading ? (
              <span className="inline-block w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            ) : mode === "unlock" ? (
              "Unlock"
            ) : (
              "Create Passphrase"
            )}
          </Button>

          {displayError && (
            <p className="text-sm text-destructive text-center">
              {displayError}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
